import { absoluteAssetUrl, canonicalProjectUrl } from "@/utils/projectRoutes";

const JSON_LD_ID = "zinnes-project-json-ld";

function text(value) {
  return String(value || "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function truncate(value, max = 160) {
  const clean = text(value);
  if (clean.length <= max) return clean;

  const clipped = clean.slice(0, max + 1);
  const lastSpace = clipped.lastIndexOf(" ");
  return `${clipped.slice(0, lastSpace > 80 ? lastSpace : max).trim()}...`;
}

function upsertMeta(selector, attrs) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attrs).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertLink(selector, attrs) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attrs).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertJsonLd(data) {
  let element = document.getElementById(JSON_LD_ID);
  if (!element) {
    element = document.createElement("script");
    element.id = JSON_LD_ID;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(data);
}

export function applyProjectSeo(project, author) {
  if (typeof document === "undefined" || !project) return;

  const name = text(project.name) || "Obra";
  const title = `${name} | Zinnes`;
  const description = truncate(project.description) || `Leia ${name} na Zinnes, plataforma de quadrinhos, mangas, webcomics e livros digitais.`;
  const canonical = canonicalProjectUrl(project);
  const image = absoluteAssetUrl(project.project_thumb || project.project_banner);
  const authorData = author || project.author || {};

  document.title = title;

  upsertMeta('meta[name="description"]', { name: "description", content: description });
  upsertLink('link[rel="canonical"]', { rel: "canonical", href: canonical });

  upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
  upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
  upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
  upsertMeta('meta[property="og:image"]', { property: "og:image", content: image });
  upsertMeta('meta[property="og:type"]', { property: "og:type", content: "article" });

  upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
  upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
  upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
  upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name,
    description,
    image,
    url: canonical,
    inLanguage: project.language?.code || project.language?.name || "pt-BR",
    isAccessibleForFree: true,
    publisher: {
      "@type": "Organization",
      name: "Zinnes",
      url: "https://www.zinnes.com.br"
    }
  };

  if (authorData.name || authorData.nickname) {
    jsonLd.author = {
      "@type": "Person",
      name: text(authorData.name || authorData.nickname)
    };

    if (authorData.nickname) {
      jsonLd.author.alternateName = text(authorData.nickname);
    }
  }

  if (project.gender?.name) {
    jsonLd.genre = text(project.gender.name);
  }

  upsertJsonLd(jsonLd);
}
